import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Award, CheckCircle, Download, Share2, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function CertificateModal({ certificate, isNew, onClose }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const pdfUrl = `${BACKEND}/api/certificates/${certificate.cert_id}/download`;
  const verifyUrl = `${window.location.origin}/verify/${certificate.cert_id}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(verifyUrl); } catch (_e) { }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="certificate-modal">
      <div className="bg-white border border-border max-w-xl w-full">
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-primary/10 text-primary inline-flex items-center justify-center"><Award className="h-5 w-5" /></div>
            <div>
              <div className="overline">{isNew ? "Congratulations" : "Certificate"}</div>
              <div className="font-display font-black text-lg tracking-tight">{isNew ? "Certificate issued!" : "Your certificate"}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-accent" aria-label="Close" data-testid="cert-modal-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6">
          <p className="text-[14px] text-muted-foreground leading-relaxed mb-5">
            {isNew
              ? "Your certificate of completion has been issued and emailed to your inbox as a PDF. You can download it any time from your dashboard."
              : "You've already completed this module. Here's your certificate."}
          </p>
          <div className="border border-border p-5 bg-accent/40 mb-5">
            <div className="overline mb-2">Recipient</div>
            <div className="font-display font-black text-[17px] tracking-tight">{certificate.user_name}</div>
            <div className="overline mt-4 mb-2">Module</div>
            <div className="font-semibold text-[14px]">{certificate.module_name}</div>
            <div className="overline mt-4 mb-2">Certificate ID</div>
            <div className="font-mono text-[13px]">{certificate.cert_id}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a data-testid="cert-download-button" href={pdfUrl} target="_blank" rel="noreferrer"
              className="h-11 px-5 inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold text-[13px] hover:bg-[#001d6b]">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button data-testid="cert-share-button" onClick={copy}
              className="h-11 px-5 inline-flex items-center gap-2 border border-border font-semibold text-[13px] hover:bg-accent">
              <Share2 className="h-4 w-4" /> Copy verification link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CourseViewer() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [mod, setMod] = useState(null);
  const [error, setError] = useState("");
  const [existingCert, setExistingCert] = useState(null);
  const [showCert, setShowCert] = useState(null); // {certificate, isNew}
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: courses }, { data: certs }] = await Promise.all([
          api.get("/me/courses"),
          api.get("/me/certificates"),
        ]);
        const found = courses.find((m) => m.slug === slug);
        if (!found) setError("This module is not assigned to your account.");
        else setMod(found);
        const cert = certs.find((c) => c.module_slug === slug);
        if (cert) setExistingCert(cert);
      } catch (e) { setError(errorMessage(e)); }
    })();
  }, [slug]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === "COURSE_COMPLETED") {
        markComplete();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, marking]);

  useEffect(() => {
    if (user) {
      window.API = {
        LMSGetValue: (param) => {
          if (param === "cmi.core.student_name") return user.full_name;
          return "";
        }
      };
      window.API_1484_11 = {
        GetValue: (param) => {
          if (param === "cmi.learner_name") return user.full_name;
          return "";
        }
      };
    }
  }, [user]);

  const markComplete = async () => {
    if (marking) return;
    setMarking(true);
    try {
      const { data } = await api.post(`/me/courses/${slug}/complete`);
      setExistingCert(data.certificate);
      setShowCert({ certificate: data.certificate, isNew: data.new });
    } catch (e) { setError(errorMessage(e)); }
    finally { setMarking(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <div className="overline mb-3">Course Viewer</div>
          <div className="font-display font-black text-2xl mb-3 tracking-tight">Module unavailable</div>
          <p className="text-[14px] text-muted-foreground mb-6">{error}</p>
          <Link to="/dashboard" className="h-10 px-5 inline-flex items-center bg-primary text-primary-foreground font-semibold text-[13px]">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (!mod) {
    return <div className="min-h-screen flex items-center justify-center"><div className="overline">Loading module...</div></div>;
  }

  return (
    <div className="h-screen flex flex-col bg-[#090E17]">
      {/* Sticky top bar */}
      <div className="h-16 flex items-center px-4 lg:px-6 border-b border-white/10 text-white">

        {/* Left side: Add flex-1 to make it take up space */}
        <div className="flex-1 flex justify-start">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[13px] font-semibold text-white/80 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
        </div>

        {/* Center: No flex-1 needed here, just the content */}
        <div className="text-center px-4 truncate">
          <div className="overline" style={{ color: "rgba(255,255,255,0.5)" }}>Training module</div>
          <div className="font-display font-bold text-[14px] truncate">{mod.name}</div>
        </div>

        {/* Right side: Add flex-1 to match the left side */}
        <div className="flex-1 flex justify-end items-center gap-2">
          {existingCert && (
            <button
              onClick={() => setShowCert({ certificate: existingCert, isNew: false })}
              className="h-9 px-4 inline-flex items-center gap-2 bg-emerald-500 text-white text-[12.5px] font-semibold hover:bg-emerald-600"
            >
              <CheckCircle className="h-4 w-4" /> Completed · View certificate
            </button>
          )}
        </div>
      </div>

      {/* Iframe */}
      <div className="flex-1 bg-white">
        <iframe
          data-testid="course-iframe"
          src={(() => {
            try {
              const url = new URL(mod.external_url);
              url.searchParams.set("learner_name", user?.full_name || "");
              return url.toString();
            } catch (e) {
              return mod.external_url;
            }
          })()}
          title={mod.name}
          className="w-full h-full border-0"
          allow="fullscreen"
        />
      </div>

      {showCert && <CertificateModal certificate={showCert.certificate} isNew={showCert.isNew} onClose={() => setShowCert(null)} />}
    </div>
  );
}
